/**
 * V2 source path: customer/midas/components/v2/MidasSidebarV2.tsx
 * V1 source: customer/midas/components/MidasSidebar.tsx + MidasSidebarHeader.tsx
 *            + MidasFleetChips.tsx (top-level container/scroll wiring)
 * Strategy: logic-copied / JSX-rewritten-in-Tailwind
 * Verified no-MUI: yes
 * Phase: 3 — Round 17 (Alt A Calm + search bar)
 *
 * V2 expanded sidebar — 280px wide on desktop, 320px wide on tablet drawer
 * (drawer width set by parent). Folds the V1 header chrome (logo + bot name +
 * chevron toggle) directly inside this component.
 *
 * Composition (top to bottom):
 *   1. Header — MIDAS logo dot + "Midas" wordmark + ChevronLeft toggle
 *   2. Platform Mode card (PlatformModeCardV2) — calm cream variant
 *   3. Fleet/History underline tab strip (MidasFleetHistoryPickerV2)
 *   4. Search bar (Round 17) — pinned above the list, filters Fleet rows by
 *      model + serial + manufacturer (case-insensitive substring). ⌘K hint
 *      shown on desktop only.
 *   5. Active tab list — Fleet (MidasFleetChipsV2) OR History (MachineThreadHistoryV2)
 *      — single ScrollArea-style scrollbar per tab.
 *   6. Bottom-only on History tab: "View full chat history →" link routing to
 *      /midas-v2/history (Phase 4).
 *
 * Logic copied from V1 MidasSidebar.tsx:
 *   - handleMachineClick: synchronous setMachineContextDirect + debounced
 *     fire-and-forget chatBotApi.setMachineContext (300ms).
 *   - dispatch wrapper around setMessages([]) + setActiveThreadId(null) +
 *     setNewThreadPending() so a fresh chat opens with the machine greeting.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, Search } from "lucide-react";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import {
  setMachineContextDirect,
  setMessages,
  setNewThreadPending,
} from "../../../../shared/chatBot/chatBotSlice";
import chatBotApi from "../../../../shared/chatBot/ChatBotApi";
import { setActiveThreadId } from "../../midasPageSlice";
import { Machine } from "../../../machinery/models/models";
import { ScrollArea } from "./ui/scroll-area";
import MachineThreadHistoryV2 from "./MachineThreadHistoryV2";
import MidasFleetChipsV2 from "./MidasFleetChipsV2";
import MidasFleetHistoryPickerV2, {
  FleetHistoryTab,
} from "./MidasFleetHistoryPickerV2";
import PlatformModeCardV2 from "./PlatformModeCardV2";

interface MidasSidebarV2Props {
  /** Optional collapse handler — if provided, renders a chevron-left in the
   *  header that closes the sidebar (desktop) / dismisses the drawer (tablet/phone). */
  onCollapse?: () => void;
  /** Initial active tab — used by the rail to deep-link to History/Fleet. */
  initialTab?: FleetHistoryTab;
  /** Called AFTER any list item click — used by phone overlay/tablet drawer to dismiss. */
  onAfterItemClick?: () => void;
  /** Whether the user has prefers-reduced-motion turned on. */
  prefersReducedMotion?: boolean;
  /** Tabbed lists' parent uses border + bg = bone-light (drawer) or transparent (desktop). */
  variant?: "desktop" | "drawer";
  /** Whether the sidebar should show the desktop ⌘K hint (false on tablet/phone). */
  showKeyboardHint?: boolean;
}

const MidasSidebarV2: React.FC<MidasSidebarV2Props> = ({
  onCollapse,
  initialTab = "fleet",
  onAfterItemClick,
  prefersReducedMotion = false,
  variant = "desktop",
  showKeyboardHint = true,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("chatBot");
  const t = (k: string, f: string) => translate(k as any, { defaultValue: f }) as string;

  const [activeTab, setActiveTab] = useState<FleetHistoryTab>(initialTab);
  const [searchTerm, setSearchTerm] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Counts shown in the picker labels
  const machinesMap = useAppSelector((s) => s.customerMachinery.machines);
  const machineryLoading = useAppSelector((s) => s.customerMachinery.loading);
  const threads = useAppSelector((s) => s.midasPage.threads);
  const fleetCount = useMemo(() => {
    if (!machinesMap || typeof machinesMap !== "object") return 0;
    let n = 0;
    Object.values(machinesMap).forEach((g) => {
      if (Array.isArray(g)) n += g.length;
    });
    return n;
  }, [machinesMap]);
  const historyCount = threads.length;

  // BUG-2026-03-20: debounce the bot-side setMachineContext fire-and-forget call
  const contextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** V1 logic copied verbatim from MidasSidebar.handleMachineClick. */
  const handleMachineClick = useCallback(
    (machine: Machine) => {
      dispatch(
        setMachineContextDirect({
          machineId: machine.id,
          model: machine.model,
          serialNumber: machine.serialNumber,
          internalNumber: machine.internalNumber,
          system: machine.system,
          manufacturerName:
            machine.customBrandName || machine.manufacturer?.name || "",
          typeName: machine.type?.name,
          axisConfigName: machine.axisConfiguration?.name,
          controlSystemBrandName: machine.controlSystemBrand?.name,
          imageContent: machine.image?.content,
          description: machine.description,
          productionYear: machine.productionYear,
        })
      );
      if (contextTimerRef.current) {
        clearTimeout(contextTimerRef.current);
      }
      contextTimerRef.current = setTimeout(() => {
        chatBotApi.setMachineContext(machine.id).catch(() => {
          /* Best-effort — bot may be offline */
        });
      }, 300);
    },
    [dispatch]
  );

  /** V1 logic from MidasFleetChips.handleCardBodyClick. */
  const handleMachineRowClick = useCallback(
    (machine: Machine) => {
      dispatch(setMessages([]));
      dispatch(setActiveThreadId(null));
      dispatch(setNewThreadPending());
      handleMachineClick(machine);
    },
    [dispatch, handleMachineClick]
  );

  // When the user switches away from Fleet, clear the search so it doesn't
  // silently filter rows the next time they come back to it.
  const handleTabChange = useCallback((tab: FleetHistoryTab) => {
    setActiveTab(tab);
    if (tab !== "fleet") setSearchTerm("");
  }, []);

  return (
    <aside
      role="navigation"
      aria-label={t("midas.aria.sidebar", "MIDAS sidebar navigation")}
      className={`flex h-full flex-col overflow-hidden bg-midas-bone ${
        variant === "drawer"
          ? ""
          : "border-r border-[rgba(44,38,32,0.08)]"
      }`}
    >
      {/* ── Header ── */}
      <div className="flex h-14 flex-shrink-0 items-center gap-2.5 border-b border-[rgba(44,38,32,0.08)] px-4">
        <span
          aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-full bg-midas-graphite-deep"
        >
          <span
            className="block h-2 w-2 rounded-full"
            style={{
              backgroundColor: "#D4572B",
              boxShadow: "0 0 4px rgba(212,87,43,0.6)",
            }}
          />
        </span>
        <span
          className="flex-1 font-sora text-[15px] font-semibold tracking-[-0.01em] text-midas-graphite"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          {variant === "drawer" ? t("midas.v2.sidebar", "Sidebar") : "Midas"}
        </span>
        {onCollapse && (
          <button
            type="button"
            onClick={onCollapse}
            aria-label={
              variant === "drawer"
                ? t("midas.aria.closeSidebar", "Close sidebar")
                : t("midas.aria.sidebarCollapsed", "Collapse sidebar")
            }
            className="flex h-11 w-11 items-center justify-center rounded-md text-midas-warm-graphite outline-none transition-colors duration-150 hover:bg-[rgba(44,38,32,0.06)] hover:text-midas-graphite focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
          >
            <ChevronLeft className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* ── Platform Mode card ── */}
      <div className="flex-shrink-0 px-4 pt-4">
        <PlatformModeCardV2 onAfterClick={onAfterItemClick} />
      </div>

      {/* ── Fleet/History underline tab strip ── */}
      <div className="flex-shrink-0 px-4 pt-4">
        <MidasFleetHistoryPickerV2
          active={activeTab}
          fleetCount={fleetCount}
          historyCount={historyCount}
          onChange={handleTabChange}
        />
      </div>

      {/* ── Search bar (Fleet tab only — pinned above the list) ── */}
      {activeTab === "fleet" && (
        <div className="flex-shrink-0 px-4 pb-2.5 pt-3.5">
          <label
            htmlFor="midas-fleet-search"
            className="flex h-11 items-center gap-2.5 rounded-lg border border-[rgba(44,38,32,0.06)] bg-[#F4EFE3] px-3 transition-colors duration-150 focus-within:border-midas-terracotta focus-within:ring-2 focus-within:ring-[rgba(212,87,43,0.18)] motion-reduce:transition-none"
          >
            <Search
              className="h-3.5 w-3.5 flex-shrink-0 text-midas-muted-stone"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            <input
              id="midas-fleet-search"
              ref={searchInputRef}
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t(
                "midas.v2.searchFleetPlaceholder",
                "Search by name, serial, or manufacturer"
              )}
              autoComplete="off"
              spellCheck={false}
              aria-label={t(
                "midas.v2.searchFleetAria",
                "Search fleet by name, serial, or manufacturer"
              )}
              className="min-w-0 flex-1 bg-transparent font-sora text-[13px] text-midas-graphite placeholder:text-midas-muted-stone focus:outline-none"
              style={{ fontFamily: "'Sora', sans-serif" }}
            />
            {showKeyboardHint && !searchTerm && (
              <span
                aria-hidden="true"
                className="flex-shrink-0 rounded-sm bg-[rgba(44,38,32,0.05)] px-1.5 py-0.5 font-mono text-[10px] font-medium text-midas-muted-stone"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                ⌘K
              </span>
            )}
          </label>
        </div>
      )}

      {/* ── Active list ── */}
      <div className={`${activeTab === "fleet" ? "" : "mt-3"} flex min-h-0 flex-1 flex-col`}>
        {/* Relative wrapper around the scroll region so the History fade overlay */}
        {/* anchors to the visible right edge of the list area only — NOT over the */}
        {/* footer "View full chat history" button. Inline-styled to avoid Tailwind */}
        {/* utility-name surprises (see feedback_tailwind_outline_none_gotcha.md). */}
        {/* #FBF6EC = midas-bone color from tailwind.config.js. */}
        <div className="relative flex min-h-0 flex-1 flex-col">
          <ScrollArea className="flex-1">
            {activeTab === "fleet" ? (
              <MidasFleetChipsV2
                onMachineClick={handleMachineRowClick}
                onAfterClick={onAfterItemClick}
                searchTerm={searchTerm}
                fleetLoading={machineryLoading}
              />
            ) : (
              <MachineThreadHistoryV2
                onAfterOpen={onAfterItemClick}
                prefersReducedMotion={prefersReducedMotion}
              />
            )}
          </ScrollArea>

          {activeTab === "history" && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                bottom: 0,
                width: "5rem",
                pointerEvents: "none",
                backgroundImage:
                  "linear-gradient(to left, #FBF6EC, rgba(251,246,236,0))",
              }}
            />
          )}
        </div>

        {activeTab === "history" && (
          <div className="flex-shrink-0 border-t border-[rgba(44,38,32,0.08)] px-3 py-2.5">
            <button
              type="button"
              onClick={() => {
                navigate("/midas-v2/history");
                if (onAfterItemClick) onAfterItemClick();
              }}
              className="group flex h-11 w-full items-center justify-between rounded-md px-2 text-left outline-none transition-colors duration-150 hover:bg-[#F4EFE3] focus-visible:ring-2 focus-visible:ring-midas-terracotta motion-reduce:transition-none"
            >
              <span
                className="font-sora text-[13px] font-medium text-midas-terracotta"
                style={{ fontFamily: "'Sora', sans-serif" }}
              >
                {t("midas.v2.viewFullHistory", "View full chat history")}
              </span>
              <ArrowRight
                className="h-4 w-4 text-midas-terracotta transition-transform duration-150 group-hover:translate-x-0.5 motion-reduce:transition-none motion-reduce:group-hover:translate-x-0"
                strokeWidth={1.75}
                aria-hidden="true"
              />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
};

export default MidasSidebarV2;
